import type { AccountMap, CostCategory, FinanceSettingsInput, KeyMapping, RepoMapping } from '@orgus/protocol';
import { COST_CATEGORIES, COST_CATEGORY_LABEL } from '@orgus/protocol';
import { ChevronDown, Landmark, Plus, Trash2 } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Panel,
  Input,
  Segmented,
  Select,
  toUiError,
  toast,
} from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useMachines, useProjects } from '../../../lib/store.ts';
import { SyncRow } from '../../finance/SyncRow.tsx';
import { saveFinanceSettings, useFinance } from '../../finance/useFinance.ts';
import type { SettingsSection } from '../registry.ts';

/** One finance source, folded to its state at rest (`summary`); its setup opens on click. */
function Block({ title, summary, children, description }: { title: string; summary: ReactNode; description: ReactNode; children: ReactNode }) {
  return (
    <Panel title={title} sub={summary} collapsible bodyClassName="flex flex-col gap-3">
      <p className="max-w-(--layout-width-prose) text-caption text-fg-subtle">{description}</p>
      {children}
    </Panel>
  );
}

/** Save part of the settings, with its own busy and error state. */
function useSave(what: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const save = async (input: FinanceSettingsInput, done?: string) => {
    setBusy(true);
    setError(null);
    try {
      await saveFinanceSettings(input);
      if (done) toast(done, { tone: 'success' });
      return true;
    } catch (e) {
      setError(toUiError(e, { what: `${what} was not saved.` }));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, save };
}

function SecretField({ label, placeholder, configured, from, hint, onSave, busy }: { label: string; placeholder: string; configured: boolean; from: string | null; hint: string | null; onSave(v: string): Promise<boolean>; busy: boolean }) {
  const [value, setValue] = useState('');
  const fromEnv = from && from !== 'settings';
  const hintId = useId();
  // The help line sits under the whole row, so the buttons line up with the field, not with its help.
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-end gap-2">
        <Input
          label={label}
          type="password"
          autoComplete="off"
          placeholder={fromEnv ? 'Not needed while the hub machine sets one' : configured ? `Saved, ends in ${hint}` : placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          wrapperClassName="w-[420px] max-w-full"
          aria-describedby={hintId}
        />
        <Button
          variant="primary"
          disabled={!value.trim() || busy}
          loading={busy}
          onClick={async () => {
            if (await onSave(value.trim())) setValue('');
          }}
        >
          Save key
        </Button>
        {configured && from === 'settings' && (
          <Button variant="ghost" disabled={busy} onClick={() => void onSave('')}>
            Remove key
          </Button>
        )}
      </div>
      <p id={hintId} className="text-caption text-fg-subtle">
        {fromEnv ? `In use: ${from} on the hub machine, which wins over a saved key.` : 'Stored on the hub machine only, never shown again.'}
      </p>
    </div>
  );
}

function MachinePicker({ value, onChange }: { value: string[]; onChange(v: string[]): void }) {
  const machines = useMachines();
  const names = value.map((id) => machines.find((m) => m.id === id)?.name ?? id);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" iconRight={<ChevronDown />} className="w-56 justify-between">
          <span className="truncate">{names.length ? names.join(', ') : 'Pick machines'}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {machines.length === 0 && <span className="block px-2 py-2 text-caption text-fg-subtle">No machines connected</span>}
        {machines.map((m) => (
          <DropdownMenuCheckboxItem
            key={m.id}
            checked={value.includes(m.id)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) => onChange(on ? [...value, m.id] : value.filter((x) => x !== m.id))}
          >
            {m.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FinanceSettingsSection() {
  const finance = useFinance();
  const projects = useProjects();
  const machines = useMachines();
  const billingSave = useSave('The billing settings');
  const githubSave = useSave('The GitHub settings');
  const computeSave = useSave('The compute rates');
  const accountsSave = useSave('The accounts');
  const [keys, setKeys] = useState<KeyMapping[]>([]);
  const [threshold, setThreshold] = useState('2');
  const [owner, setOwner] = useState('');
  const [ownerKind, setOwnerKind] = useState<'org' | 'user'>('org');
  const [repos, setRepos] = useState<RepoMapping[]>([]);
  const [rates, setRates] = useState<Record<string, string>>({});
  const [accounts, setAccounts] = useState<AccountMap | null>(null);
  const [credit, setCredit] = useState('');
  const version = finance?.version;

  useEffect(() => {
    if (!finance) return;
    const s = finance.settings;
    setKeys(s.keys);
    setThreshold(String(Math.round(s.varianceThreshold * 1000) / 10));
    setOwner(s.githubOwner);
    setOwnerKind(s.githubOwnerKind);
    setRepos(s.repos);
    setRates(Object.fromEntries(s.computeRates.map((r) => [r.machineId, String(r.hourlyRate)])));
    setAccounts(s.accounts);
    setCredit(s.creditAccount);
    // Reset the form when the saved settings change, not on every keystroke.
  }, [version, !!finance]);

  if (!finance || !accounts) {
    return <EmptyState icon={<Landmark />} title="This hub has no finance module" description="Update Orgus on the hub machine and restart it to keep other costs, reconcile billing and export to your accountant." />;
  }
  const projectOpts = projects.map((p) => ({ value: p.id, label: p.name }));
  const thresholdNum = Number(threshold) / 100;
  const autoRepos = projects
    .map((p) => ({ p, repo: /github\.com[:/]([A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+?)(?:\.git)?\/?$/.exec(p.remoteUrl ?? '')?.[1] }))
    .filter((x): x is { p: (typeof projects)[number]; repo: string } => !!x.repo);

  return (
    <div className="flex flex-col gap-3">
      <Block
        title="Anthropic billing"
        summary={finance.sync.billing.configured ? `Connected, ${keys.length} ${keys.length === 1 ? 'key' : 'keys'}, flags above ${threshold}%` : 'Not connected'}
        description={
          <>
            Every night Orgus pulls your organization usage and cost report and reconciles it with what transcripts recorded. It needs an Admin API key (sk-ant-admin01-...), which an
            organization admin creates in the Claude Console under Settings, Admin keys. Or set ORGUS_ANTHROPIC_ADMIN_KEY on the hub machine.
          </>
        }
      >
        <SyncRow status={finance.sync.billing} />
        <SecretField
          label="Admin API key"
          placeholder="sk-ant-admin01-..."
          configured={finance.sync.billing.configured}
          from={finance.sync.billing.credentialFrom}
          hint={finance.sync.billing.hint}
          busy={billingSave.busy}
          onSave={(v) => billingSave.save({ anthropicAdminKey: v }, v ? 'Admin key saved' : 'Admin key removed')}
        />
        <div className="flex flex-col gap-2">
          <span className="text-caption font-medium text-fg-muted">API keys and the machines that use them</span>
          {keys.map((k, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input aria-label="Key id" placeholder="apikey_01Rj2N8S..." value={k.keyId} onChange={(e) => setKeys(keys.map((x, j) => (j === i ? { ...x, keyId: e.target.value.trim() } : x)))} wrapperClassName="w-72" className="font-mono" />
              <Input aria-label="Key name" placeholder="Build box" value={k.label} onChange={(e) => setKeys(keys.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} wrapperClassName="w-44" />
              <MachinePicker value={k.machineIds} onChange={(ids) => setKeys(keys.map((x, j) => (j === i ? { ...x, machineIds: ids } : x)))} />
              <IconButton label={`Remove key ${k.label || k.keyId}`} icon={<Trash2 />} variant="danger" size="sm" onClick={() => setKeys(keys.filter((_, j) => j !== i))} />
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2">
            <Button variant="secondary" size="sm" icon={<Plus />} onClick={() => setKeys([...keys, { keyId: '', label: '', machineIds: [] }])}>
              Add key
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            label="Flag a variance above (%)"
            inputMode="decimal"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            wrapperClassName="w-56"
            error={!(thresholdNum > 0 && thresholdNum <= 1) ? 'A percentage between 0 and 100.' : undefined}
          />
          <Button
            variant="primary"
            loading={billingSave.busy}
            disabled={!(thresholdNum > 0 && thresholdNum <= 1) || keys.some((k) => !k.keyId)}
            onClick={() => void billingSave.save({ keys, varianceThreshold: thresholdNum }, 'Billing settings saved')}
          >
            Save billing settings
          </Button>
        </div>
        {billingSave.error && <ErrorState error={billingSave.error} variant="inline" />}
      </Block>

      <Block
        title="GitHub"
        summary={finance.sync.github.credentialFrom !== null ? `Connected${owner ? `, ${owner}` : ''}, ${repos.length} ${repos.length === 1 ? 'repository' : 'repositories'}` : 'Not connected'}
        description="CI minutes come from the GitHub billing usage report of an organization or user; closed pull requests give cost per merged PR and the rework rate. Use a fine-grained token with Administration read (billing) and Pull requests read, or set ORGUS_GITHUB_TOKEN on the hub machine."
      >
        <SyncRow status={finance.sync.github} />
        <SecretField
          label="GitHub token"
          placeholder="github_pat_..."
          configured={finance.sync.github.credentialFrom !== null}
          from={finance.sync.github.credentialFrom}
          hint={finance.sync.github.hint}
          busy={githubSave.busy}
          onSave={(v) => githubSave.save({ githubToken: v }, v ? 'GitHub token saved' : 'GitHub token removed')}
        />
        <div className="flex flex-wrap items-end gap-2">
          <Input label="Owner" placeholder="acme" value={owner} onChange={(e) => setOwner(e.target.value.trim())} wrapperClassName="w-56" />
          <Segmented<'org' | 'user'>
            label="Owner kind"
            size="md"
            options={[
              { value: 'org', label: 'Organization' },
              { value: 'user', label: 'User' },
            ]}
            value={ownerKind}
            onChange={setOwnerKind}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-caption font-medium text-fg-muted">Repositories and their projects</span>
          {autoRepos.length > 0 && (
            <p className="text-caption text-fg-subtle">
              Found from project remotes: {autoRepos.map((x) => `${x.repo} (${x.p.name})`).join(', ')}. Add a row only for repositories that differ.
            </p>
          )}
          {repos.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input aria-label="Repository" placeholder="acme/app" value={r.repo} onChange={(e) => setRepos(repos.map((x, j) => (j === i ? { ...x, repo: e.target.value.trim() } : x)))} wrapperClassName="w-64" className="font-mono" />
              <Select aria-label="Project" options={projectOpts} placeholder="Pick a project" value={r.projectId} onValueChange={(v) => setRepos(repos.map((x, j) => (j === i ? { ...x, projectId: v } : x)))} wrapperClassName="w-56" />
              <IconButton label={`Remove ${r.repo || 'repository'}`} icon={<Trash2 />} variant="danger" size="sm" onClick={() => setRepos(repos.filter((_, j) => j !== i))} />
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" icon={<Plus />} onClick={() => setRepos([...repos, { repo: '', projectId: '' }])}>
              Add repository
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={githubSave.busy}
              disabled={repos.some((r) => !r.repo || !r.projectId)}
              onClick={() => void githubSave.save({ githubOwner: owner, githubOwnerKind: ownerKind, repos }, 'GitHub settings saved')}
            >
              Save GitHub settings
            </Button>
          </div>
        </div>
        {githubSave.error && <ErrorState error={githubSave.error} variant="inline" />}
      </Block>

      <Block
        title="Node compute"
        summary={`${Object.values(rates).filter((r) => Number(r) > 0).length} of ${machines.length} machines have a rate`}
        description="Owned machines cost their hourly rate for every hour they are online, split across the projects that ran sessions on them that day. Leave a rate empty for no cost.">
        {machines.length === 0 ? (
          <p className="text-caption text-fg-subtle">No machines connected yet.</p>
        ) : (
          <div className="grid max-w-[680px] grid-cols-1 gap-2 sm:grid-cols-2">
            {machines.map((m) => (
              <Input
                key={m.id}
                label={`${m.name} (USD per hour)`}
                inputMode="decimal"
                placeholder="0.00"
                value={rates[m.id] ?? ''}
                onChange={(e) => setRates({ ...rates, [m.id]: e.target.value })}
                error={rates[m.id] && !(Number(rates[m.id]) >= 0 && Number(rates[m.id]) <= 1000) ? 'Between $0 and $1,000.' : undefined}
              />
            ))}
          </div>
        )}
        <div>
          <Button
            variant="primary"
            loading={computeSave.busy}
            disabled={Object.values(rates).some((v) => v !== '' && !(Number(v) >= 0 && Number(v) <= 1000))}
            onClick={() =>
              void computeSave.save(
                { computeRates: Object.entries(rates).filter(([, v]) => v !== '' && Number(v) > 0).map(([machineId, v]) => ({ machineId, hourlyRate: Number(v) })) },
                'Compute rates saved',
              )
            }
          >
            Save rates
          </Button>
        </div>
        {computeSave.error && <ErrorState error={computeSave.error} variant="inline" />}
      </Block>

      <Block
        title="Accounting export"
        summary={credit ? `Credits ${credit}` : 'Not set'}
        description="The expense account each category posts to in the journal export, and the account the journal credits.">
        <div className="grid max-w-[680px] grid-cols-1 gap-2 sm:grid-cols-2">
          {COST_CATEGORIES.map((c: CostCategory) => (
            <Input key={c} label={COST_CATEGORY_LABEL[c]} value={accounts[c]} onChange={(e) => setAccounts({ ...accounts, [c]: e.target.value })} maxLength={80} />
          ))}
          <Input label="Credit account" value={credit} onChange={(e) => setCredit(e.target.value)} maxLength={80} />
        </div>
        <div>
          <Button
            variant="primary"
            loading={accountsSave.busy}
            disabled={COST_CATEGORIES.some((c) => !accounts[c].trim()) || !credit.trim()}
            onClick={() => void accountsSave.save({ accounts, creditAccount: credit }, 'Accounts saved')}
          >
            Save accounts
          </Button>
        </div>
        {accountsSave.error && <ErrorState error={accountsSave.error} variant="inline" />}
      </Block>
    </div>
  );
}

const section: SettingsSection = {
  id: 'finance',
  title: 'Finance',
  description: 'Billing reconciliation, CI minutes, compute rates and accounts.',
  order: 20,
  group: 'money',
  keywords: ['reconciliation', 'invoices', 'accounting', 'ci minutes', 'compute'],
  icon: Landmark,
  Component: FinanceSettingsSection,
  needs: { module: 'finance', feature: 'Finance settings', plural: true, instead: 'Spend shows what each session and model costs.' },
};
export default section;
